// Inspecteur · Verwerking › Doel detail — the per-PURPOSE Art.30 home (Phase A, MASTER-PICTURE §1.2).
// Mirrors the legacy doel sub-tabs: Algemeen + Categorieën gegevens + Betrokkenen + Wettelijke grondslag +
// Ontvangers, each scoped to this doel via the ?doelId child endpoints. Built on shadcn + TanStack Query.
import { useState, type ReactNode } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { enumLabel } from "../../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);
type Row = Record<string, unknown>;
const selectCls = "h-8 w-full max-w-sm rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30";

export function DoelDetailRoute() {
  const { id, doelId } = useParams({ from: "/verwerkingen/$id/doelen/$doelId" });
  const { t } = useI18n();
  const doelQ = useQuery({ queryKey: ["doel", id, doelId], queryFn: () => api.getVerwerkingDoel(id, doelId) });
  const doel = (doelQ.data ?? null) as Row | null;

  return (
    <section>
      <Link to="/verwerkingen/$id" params={{ id }} className="mb-4 inline-block text-sm text-primary hover:underline">
        ← {t("verw.detail.title")}
      </Link>
      <PageHeader
        title={String(doel?.name ?? t("doel.fallbackTitle"))}
        description={t("doel.detail.sub")}
        actions={doel ? <Badge variant="secondary">{enumLabel(t, "verwerkingStatus", doel.status ?? "active")}</Badge> : undefined}
      />
      {doelQ.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(doelQ.error)}</p>}

      <div className="grid gap-4">
        <AlgemeenCard verwerkingId={id} doelId={doelId} doel={doel} loading={doelQ.isLoading} />
        <CatalogPanel
          title={t("doel.cat.pdc")} verwerkingId={id} doelId={doelId} kind="personal-data-categories"
          idField="personalDataCategoryId" optionsQueryKey={["lib", "pdc"]} optionsFn={() => api.listPersonalDataCategories()}
          optionLabel={(o) => String(o.displayName ?? o.name)} rowLabel={(r) => String(r.displayName ?? r.name ?? "—")}
          rowExtra={(r) => (r.kind === "special" || r.isSpecialCategory ? <Badge variant="destructive">{t("doel.special")}</Badge> : null)}
          extraBody={(o) => ({ isSpecialCategory: o.kind === "special" })}
        />
        <CatalogPanel
          title={t("doel.cat.subjects")} verwerkingId={id} doelId={doelId} kind="data-subject-categories"
          idField="dataSubjectCategoryId" optionsQueryKey={["lib", "dsc"]} optionsFn={() => api.listDataSubjectCategories()}
          optionLabel={(o) => String(o.name)} rowLabel={(r) => String(r.name ?? "—")}
        />
        <CatalogPanel
          title={t("doel.cat.legalBasis")} verwerkingId={id} doelId={doelId} kind="legal-basis"
          idField="legalBasisId" optionsQueryKey={["lib", "legal"]} optionsFn={() => api.listLegalBases()}
          optionLabel={(o) => [o.displayName, o.articleRef].filter(Boolean).join(" — ")} rowLabel={(r) => String(r.displayName ?? r.articleRef ?? "—")}
        />
        <RecipientsPanel verwerkingId={id} doelId={doelId} />
        <RisicoPanel verwerkingId={id} doelId={doelId} />
      </div>
    </section>
  );
}

// Per-doel BIV/Risico assessment (Issue 16 / legacy doel "Risico" tab): confidentiality / integrity /
// availability on the 5-point scale + a rationale per axis. get + upsert via the /doelen/:id/biv endpoint.
const BIV_SCALE = ["very_low", "low", "medium", "high", "very_high"];
function RisicoPanel({ verwerkingId, doelId }: { verwerkingId: string; doelId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ["doel-biv", verwerkingId, doelId];
  const q = useQuery({ queryKey: key, queryFn: () => api.getDoelBiv(verwerkingId, doelId) as Promise<Row | null> });
  const row = q.data ?? null;
  const [edits, setEdits] = useState<Record<string, string>>({});
  const val = (k: string, dflt: string) => edits[k] ?? (row?.[k] != null ? String(row[k]) : dflt);
  const set = (k: string, v: string) => setEdits((s) => ({ ...s, [k]: v }));
  const save = useMutation({
    mutationFn: () => api.upsertDoelBiv(verwerkingId, doelId, {
      confidentiality: val("confidentiality", "medium"), integrity: val("integrity", "medium"), availability: val("availability", "medium"),
      confidentialityMotivation: val("confidentialityMotivation", ""), integrityMotivation: val("integrityMotivation", ""), availabilityMotivation: val("availabilityMotivation", ""),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); setEdits({}); },
  });
  const axes: { k: string; mk: string; label: string }[] = [
    { k: "confidentiality", mk: "confidentialityMotivation", label: t("doel.risico.c") },
    { k: "integrity", mk: "integrityMotivation", label: t("doel.risico.i") },
    { k: "availability", mk: "availabilityMotivation", label: t("doel.risico.a") },
  ];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("doel.risico")}</CardTitle></CardHeader>
      <CardContent>
        {q.isLoading ? <Skeleton className="h-24 w-2/3" /> : q.error ? (
          <p className="text-sm text-destructive">{t("crud.failed")} {errText(q.error)}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {axes.map((a) => (
              <div key={a.k} className="grid gap-2 sm:grid-cols-[10rem_1fr] sm:items-center">
                <div className="flex items-center gap-2">
                  <Label htmlFor={`biv-${a.k}`}>{a.label}</Label>
                  <select id={`biv-${a.k}`} className={selectCls} value={val(a.k, "medium")} onChange={(e) => set(a.k, e.target.value)}>
                    {BIV_SCALE.map((s) => <option key={s} value={s}>{t(`doel.scale.${s}`)}</option>)}
                  </select>
                </div>
                <Input value={val(a.mk, "")} onChange={(e) => set(a.mk, e.target.value)} placeholder={t("doel.risico.motiv")} />
              </div>
            ))}
            <Can perm="verwerking.update">
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? t("verw.saving") : t("verw.save")}</Button>
                {save.isError && <span className="text-sm text-destructive">{errText(save.error)}</span>}
              </div>
            </Can>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Algemeen: editable Naam + Omschrijving + Status (the doel's own fields).
function AlgemeenCard({ verwerkingId, doelId, doel, loading }: { verwerkingId: string; doelId: string; doel: Row | null; loading: boolean }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.updateVerwerkingDoel(verwerkingId, doelId, {
      ...(name !== null ? { name } : {}), ...(description !== null ? { description } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doel", verwerkingId, doelId] }); qc.invalidateQueries({ queryKey: ["verwerking-child", verwerkingId, "doelen"] }); setName(null); setDescription(null); },
  });
  const nameVal = name ?? String(doel?.name ?? "");
  const descVal = description ?? String(doel?.description ?? "");
  const dirty = name !== null || description !== null;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("doel.general")}</CardTitle></CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-20 w-2/3" /> : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="doel-name">{t("doel.name")}</Label>
              <Input id="doel-name" value={nameVal} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="doel-desc">{t("doel.description")}</Label>
              <Textarea id="doel-desc" rows={3} value={descVal} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <Can perm="verwerking.update">
              <div className="flex items-center gap-2">
                <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? t("verw.saving") : t("verw.save")}
                </Button>
                {dirty && <Button size="sm" variant="ghost" onClick={() => { setName(null); setDescription(null); }}>{t("common.cancel")}</Button>}
              </div>
            </Can>
            {save.isError && <p className="text-sm text-destructive">{errText(save.error)}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// A catalog-backed Art.30 junction panel scoped to this doel: list + pick-from-catalog add + remove.
function CatalogPanel(props: {
  title: string; verwerkingId: string; doelId: string; kind: string; idField: string;
  optionsQueryKey: string[]; optionsFn: () => Promise<unknown[]>;
  optionLabel: (o: Row) => string; rowLabel: (r: Row) => string;
  rowExtra?: (r: Row) => ReactNode; extraBody?: (o: Row) => Record<string, unknown>;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ["doel-child", props.verwerkingId, props.doelId, props.kind];
  const linked = useQuery({ queryKey: key, queryFn: () => api.listVerwerkingChild(props.verwerkingId, props.kind, props.doelId) });
  const options = useQuery({ queryKey: props.optionsQueryKey, queryFn: props.optionsFn });
  const [sel, setSel] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: () => {
      const opt = ((options.data ?? []) as Row[]).find((o) => String(o.id) === sel) ?? {};
      return api.addVerwerkingChild(props.verwerkingId, props.kind, {
        [props.idField]: sel, verwerkingDoelId: props.doelId, ...(props.extraBody ? props.extraBody(opt) : {}),
      });
    },
    onSuccess: () => { setSel(""); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (cid: string) => api.removeVerwerkingChild(props.verwerkingId, props.kind, cid), onSuccess: invalidate });

  const rows = (linked.data ?? []) as Row[];
  const opts = (options.data ?? []) as Row[];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{props.title}</CardTitle></CardHeader>
      <CardContent>
        {linked.isLoading ? <Skeleton className="h-5 w-2/3" /> : (
          <>
            {linked.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(linked.error)}</p>}
            {!linked.error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("doel.nothingLinked")}</p>}
            <ul className="m-0 list-none p-0">
              {rows.map((r) => (
                <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">{props.rowLabel(r)}</span>
                    {props.rowExtra?.(r)}
                  </span>
                  <Can perm="verwerking.update">
                    <Button variant="ghost" size="xs" className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(String(r.id))}>
                      {t("verw.detail.fs.remove")}
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
            <Can perm="verwerking.write">
              <div className="mt-3 flex items-center gap-2">
                <select value={sel} onChange={(e) => setSel(e.target.value)} aria-label={props.title} className={selectCls}>
                  <option value="">{t("crud.select")}</option>
                  {opts.map((o) => <option key={String(o.id)} value={String(o.id)}>{props.optionLabel(o)}</option>)}
                </select>
                <Button size="sm" disabled={add.isPending || !sel} onClick={() => add.mutate()}>
                  {add.isPending ? t("crud.creating") : t("verw.detail.fs.add")}
                </Button>
              </div>
              {add.isError && <p className="mt-2 text-sm text-destructive">{errText(add.error)}</p>}
            </Can>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Recipients (Art.30(1)(d)) — free-text categories of recipients, scoped to this doel.
function RecipientsPanel({ verwerkingId, doelId }: { verwerkingId: string; doelId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ["doel-child", verwerkingId, doelId, "recipients"];
  const linked = useQuery({ queryKey: key, queryFn: () => api.listVerwerkingChild(verwerkingId, "recipients", doelId) });
  const [val, setVal] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: () => api.addVerwerkingChild(verwerkingId, "recipients", { recipientCategory: val.trim(), verwerkingDoelId: doelId }),
    onSuccess: () => { setVal(""); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (cid: string) => api.removeVerwerkingChild(verwerkingId, "recipients", cid), onSuccess: invalidate });
  const rows = (linked.data ?? []) as Row[];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("doel.recipients")}</CardTitle></CardHeader>
      <CardContent>
        {linked.isLoading ? <Skeleton className="h-5 w-2/3" /> : (
          <>
            {!linked.error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("doel.recipients.empty")}</p>}
            <ul className="m-0 list-none p-0">
              {rows.map((r) => (
                <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                  <span className="truncate font-medium">{String(r.recipientCategory ?? "—")}</span>
                  <Can perm="verwerking.update">
                    <Button variant="ghost" size="xs" className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(String(r.id))}>
                      {t("verw.detail.fs.remove")}
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
            <Can perm="verwerking.write">
              <div className="mt-3 flex items-center gap-2">
                <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={t("doel.recipients.placeholder")} className="max-w-sm" />
                <Button size="sm" disabled={add.isPending || !val.trim()} onClick={() => add.mutate()}>
                  {add.isPending ? t("crud.creating") : t("verw.detail.fs.add")}
                </Button>
              </div>
              {add.isError && <p className="mt-2 text-sm text-destructive">{errText(add.error)}</p>}
            </Can>
          </>
        )}
      </CardContent>
    </Card>
  );
}
